import type { UserNode } from '@revelio/data-access';

import { findFilterById } from '../../../filters/engine/filterRegistry';
import {
  type Filter,
  FilterId,
  FilterType,
  isMultiFilter,
  type TreeFilter,
} from '../../../filters/types/filters';
import { buildTreeFilterNodes, wrapExprNode } from './node-builders';
import { TalentDiscoveryV3Tags } from './types';

/** Shared by the Company and Industry filters — both sit on the RICS tree. */
export const RICS_LEVEL_ID_TO_TAG: Record<string, TalentDiscoveryV3Tags> = {
  company: TalentDiscoveryV3Tags.CompanyId,
  rics_k10: TalentDiscoveryV3Tags.RicsK10,
  rics_k50: TalentDiscoveryV3Tags.RicsK50,
  rics_k400: TalentDiscoveryV3Tags.RicsK400,
};

/**
 * Build company filter node from filter state.
 * Uses the generic buildTreeFilterNodes function to handle tree filter logic.
 */
export const buildCompanyFilter = (filters: Filter[]): UserNode | null => {
  const companyFilter = findFilterById(filters, FilterId.COMPANY);
  if (!companyFilter || companyFilter.type !== FilterType.TREE) {
    return null;
  }

  const exprNode = buildTreeFilterNodes(companyFilter, RICS_LEVEL_ID_TO_TAG);
  if (!exprNode) return null;

  return wrapExprNode(exprNode, companyFilter.isNot);
};

/**
 * Build multi company filter node from filter state.
 * Each instance becomes a node, and instances are ANDed together.
 * Returns an array of nodes when there are multiple instances (to avoid nested AND nodes).
 */
export const buildMultiCompanyFilter = (
  filters: Filter[]
): UserNode | UserNode[] | null => {
  const multiCompany = findFilterById(filters, FilterId.MULTI_COMPANY);

  if (!multiCompany || !isMultiFilter(multiCompany)) {
    return null;
  }

  if (multiCompany.instances.length === 0) {
    return null;
  }

  const instanceNodes = multiCompany.instances
    .filter((inst): inst is TreeFilter => inst?.type === FilterType.TREE)
    .map((instance) => {
      const exprNode = buildTreeFilterNodes(instance, RICS_LEVEL_ID_TO_TAG);
      if (!exprNode) return null;

      return wrapExprNode(exprNode, instance.isNot);
    })
    .filter((node): node is UserNode => node !== null);

  if (instanceNodes.length === 0) return null;
  if (instanceNodes.length === 1) {
    const [singleNode] = instanceNodes;
    return singleNode ?? null;
  }

  return instanceNodes;
};
